import "./RequestRescanButton.scss";

import React, { useState, useRef } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import Modal from "react-modal";
import { useDispatch } from "react-redux";
import { useLocalStorage } from "react-use";

import { requestSlideRescan } from "shared/actions/slideDetailActions";

import { ReactComponent as ExclamationIcon } from "icons/exclamation.svg";
import { AppDispatch } from "store/store";

import LinkButton from "./LinkButton";

interface RescanRequest {
  timestamp: number;
  reason: string;
}

interface RequestRescanButtonProps extends React.PropsWithChildren<any> {
  slide_id: number;
  noicon?: boolean;
}

/*
 * Assumes permissions, etc. have already been checked before it gets rendered. Pops open a modal when clicked.
 * Modal either displays a textarea for entering a reason for the rescan request, along with Send/Cancel buttons,
 * or a message displaying a previous rescan request you submitted. (Using localStorage until we have a read endpoint.)
 * Icon itself is rendered black if no rescan request has been submitted from your device, gray otherwise.
 */
const RequestRescanButton = (props: RequestRescanButtonProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const [modalOpen, setModalOpen] = useState(false);
  const reasonRef = useRef<HTMLTextAreaElement | null>(null);

  const [requested_rescans, set_requested_rescans] = useLocalStorage<{
    [key: number]: RescanRequest;
  }>("requested_rescans", {});
  const priorRequest: RescanRequest | undefined = requested_rescans
    ? requested_rescans[props.slide_id]
    : undefined;

  useHotkeys(
    "shift+1",
    () => {
      setModalOpen(!modalOpen);
    },
    {},
    [modalOpen]
  );

  const [enableSend, setEnableSend] = useState<boolean>(false);

  return props.slide_id !== 0 ? (
    <>
      <LinkButton
        nocolor
        className={`request_rescan_button${
          priorRequest ? " request_rescan_button__disabled" : ""
        }`}
        disabled={!priorRequest !== undefined}
        onClick={() => {
          setModalOpen(!modalOpen);
        }}
        title={priorRequest ? "RESCAN REQUEST ALREADY SENT" : "REQUEST RESCAN"}
      >
        {props.noicon ? "" : <ExclamationIcon className="icon-12" />}
        {props.children}
      </LinkButton>
      <Modal
        isOpen={modalOpen}
        onRequestClose={() => {
          setModalOpen(false);
        }}
        contentLabel="Request Rescan"
        className="Modal"
        overlayClassName="Overlay"
      >
        <div className="request_rescan__dialog">
          {priorRequest !== undefined ? (
            <>
              <h3>REQUEST ALREADY SENT</h3>
              <div>
                You requested a rescan from this device on{" "}
                {new Date(priorRequest.timestamp).toLocaleString()}.
              </div>
              <div>
                SLIDE ID:<strong>{props.slide_id}</strong>
              </div>
              <div>REASON:</div>
              <div className="reason_box">
                <pre className="default_font">{priorRequest?.reason}</pre>
              </div>
              <nav>
                <LinkButton
                  onClick={() => {
                    const copy = { ...requested_rescans };
                    delete copy[props.slide_id]; // remove the key for this slide
                    set_requested_rescans(copy);
                  }}
                >
                  Reset Device
                </LinkButton>
              </nav>
            </>
          ) : (
            <>
              <h3>REQUEST RESCAN</h3>
              <div>
                SLIDE ID:<strong>{props.slide_id}</strong>
              </div>
              <div>REASON:</div>
              <textarea
                autoFocus={true}
                rows={5}
                cols={40}
                placeholder=""
                ref={reasonRef}
                onChange={() => {
                  setEnableSend(
                    (reasonRef.current?.value || "").trim().length !== 0
                  );
                }}
              />
              <nav>
                <button
                  disabled={!enableSend}
                  onClick={() => {
                    const reason = (reasonRef.current?.value || "").trim();
                    if (reason.length !== 0) {
                      const entry: { [key: number]: RescanRequest } = {};
                      entry[props.slide_id] = { timestamp: Date.now(), reason };
                      set_requested_rescans({ ...requested_rescans, ...entry });
                      dispatch(requestSlideRescan(props.slide_id, reason));
                      setModalOpen(false);
                    }
                  }}
                >
                  SEND
                </button>
                <button
                  onClick={() => {
                    setModalOpen(false);
                  }}
                >
                  CANCEL
                </button>
              </nav>
            </>
          )}
        </div>
      </Modal>
    </>
  ) : (
    <></>
  );
};

export default RequestRescanButton;
